/**
 * Lectura de códigos (lector USB/Bluetooth o tipeo + Enter).
 *
 * Los lectores se comportan como un teclado: escriben el código muy rápido y
 * mandan Enter. El backend busca primero por código de barras exacto (acepta
 * UPC-A y EAN-13) y después por SKU sin distinguir mayúsculas.
 */

import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

import { ApiError, api } from "@/lib/api.ts";
import type { ProductOut } from "@/types/api.ts";

/** Producto con ese código de barras o SKU, o null si no existe. */
export async function lookupProduct(code: string): Promise<ProductOut | null> {
  const trimmed = code.trim();
  if (!trimmed) return null;
  try {
    return await api.get<ProductOut>("/products/lookup", { searchParams: { code: trimmed } });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export interface ScanHandlers {
  onProduct: (product: ProductOut) => void;
  onNotFound?: (code: string) => void;
  onError?: (code: string) => void;
}

/**
 * Procesa las lecturas en orden, una tras otra, sin perder ninguna aunque
 * lleguen más rápido de lo que responde la API (conteo unidad por unidad).
 * Recuerda los códigos ya resueltos: releer el mismo producto es instantáneo.
 */
export class ScanQueue {
  private chain: Promise<void> = Promise.resolve();
  private readonly cache = new Map<string, ProductOut>();
  private readonly lookup: (code: string) => Promise<ProductOut | null>;

  constructor(lookup: (code: string) => Promise<ProductOut | null> = lookupProduct) {
    this.lookup = lookup;
  }

  push(code: string, handlers: ScanHandlers): Promise<void> {
    const value = code.trim();
    if (!value) return this.chain;
    this.chain = this.chain.then(async () => {
      try {
        const product = this.cache.get(value) ?? (await this.lookup(value));
        if (product) {
          this.cache.set(value, product);
          handlers.onProduct(product);
        } else {
          handlers.onNotFound?.(value);
        }
      } catch {
        handlers.onError?.(value);
      }
    });
    return this.chain;
  }
}

/**
 * Distingue un lector de una persona escribiendo: el lector manda todos los
 * caracteres con muy poco tiempo entre sí y termina con Enter.
 */
export class ScanBuffer {
  private chars = "";
  private last = 0;
  private readonly maxGapMs: number;
  private readonly minLength: number;

  constructor(maxGapMs = 50, minLength = 4) {
    this.maxGapMs = maxGapMs;
    this.minLength = minLength;
  }

  /** Devuelve el código cuando se completa una lectura; si no, null. */
  feed(key: string, time: number): string | null {
    if (key === "Enter") {
      const code = time - this.last <= this.maxGapMs ? this.chars : "";
      this.chars = "";
      return code.length >= this.minLength ? code : null;
    }
    if (key.length !== 1) return null; // Shift, flechas, etc.
    if (this.chars && time - this.last > this.maxGapMs) this.chars = "";
    this.chars += key;
    this.last = time;
    return null;
  }
}

export interface ScanQueueState {
  enqueue: (code: string) => void;
  /** Lecturas esperando respuesta del servidor. */
  pending: number;
  /** La última consulta falló (sin red o servidor caído); se limpia al próximo éxito. */
  failed: boolean;
}

/** Cola de lecturas con los handlers más recientes del componente. */
export function useScanQueue(handlers: ScanHandlers): ScanQueueState {
  const queue = useRef<ScanQueue | null>(null);
  const latest = useRef(handlers);
  const [pending, setPending] = useState(0);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    latest.current = handlers;
  });
  const enqueue = useCallback((code: string) => {
    if (!code.trim()) return;
    queue.current ??= new ScanQueue();
    setPending((n) => n + 1);
    void queue.current
      .push(code, {
        onProduct: (p) => {
          setFailed(false);
          latest.current.onProduct(p);
        },
        onNotFound: (c) => {
          setFailed(false);
          latest.current.onNotFound?.(c);
        },
        onError: (c) => {
          setFailed(true);
          latest.current.onError?.(c);
        },
      })
      .finally(() => setPending((n) => n - 1));
  }, []);
  return { enqueue, pending, failed };
}

/** true mientras el navegador tiene red. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  return online;
}

/** true si el foco está en un campo de texto distinto de `except`. */
export function useTypingElsewhere(except: RefObject<HTMLElement | null>): boolean {
  const [elsewhere, setElsewhere] = useState(false);
  useEffect(() => {
    const update = () => {
      const active = document.activeElement;
      setElsewhere(isEditable(active) && active !== except.current);
    };
    // En focusout el activeElement todavía es el que se va: se mira en el próximo tick.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onFocusOut = () => {
      timer = setTimeout(update, 0);
    };
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, [except]);
  return elsewhere;
}

export function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  );
}

/**
 * Captura lecturas del lector aunque el foco no esté en un campo (p. ej. el
 * cajero tocó un botón del carrito). Los campos de texto manejan su Enter.
 */
export function useScannerCapture(onCode: (code: string) => void, enabled = true): void {
  const latest = useRef(onCode);
  useEffect(() => {
    latest.current = onCode;
  });
  useEffect(() => {
    if (!enabled) return;
    const buffer = new ScanBuffer();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || e.metaKey || isEditable(e.target)) return;
      const code = buffer.feed(e.key, e.timeStamp);
      if (code) {
        e.preventDefault(); // que el Enter no "presione" el botón enfocado
        latest.current(code);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}

/** Estado del lector para la UI: verde listo o leyendo, rojo en pausa. */
export type ScanReadiness = "ready" | "reading" | "paused";

/** Borde del campo de lectura según el estado (verde listo, rojo en pausa). */
export function scanFieldClass(state: ScanReadiness): string {
  return state === "paused"
    ? "border-red-300 bg-red-50/40 focus:border-red-500 focus:ring-red-500/15"
    : "border-emerald-400 bg-emerald-50/40 focus:border-emerald-600 focus:ring-emerald-500/20";
}
